import React, { useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';

interface PluginCardProps {
  id: string;
  name: string;
  slug: string;
  description: string;
  price: number;
  version: string;
  imageUrl?: string;
  features?: string[];
  compatibility: string[];
  tags: string[];
  onBuyNow: (pluginId: string, slug: string) => void;
  onViewDetails: (slug: string) => void;
  owned?: boolean;
}

function PluginCardC({ 
  id, 
  name, 
  slug,
  description, 
  price, 
  version,
  imageUrl, 
  features = [],
  compatibility = [],
  tags = [],
  onBuyNow,
  onViewDetails,
  owned = false,
}: PluginCardProps) {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleBuyNow = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (owned) return;
    onBuyNow(id, slug);
  };

  const handleViewDetails = () => {
    onViewDetails(slug);
  };

  const displayImage = imageError || !imageUrl 
    ? '/new_logo.png' 
    : (slug === 'spreadra' 
        ? 'https://sonicmakers-uploads.lon1.digitaloceanspaces.com/public/plugins/spreadra/images/spreadra-interface.png'
        : imageUrl);

  const displayTags = tags.slice(0, 3); // Show only first 3 tags
  const displayFeatures = features.slice(0, 2); // Show only first 2 features

  return (
    <div 
      className="group flex h-full cursor-pointer flex-col transition-all duration-300 hover:scale-105 bg-gray-800/50 border border-gray-700 hover:border-purple-500/50 rounded-lg overflow-hidden backdrop-blur-sm"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleViewDetails}
    >
      {/* Plugin Image */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={displayImage}
          alt={name}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          onError={() => setImageError(true)}
        />
        
        {/* Version Badge */}
        <div className="absolute top-2 right-2 z-10">
          <span className="bg-purple-500/90 text-white text-sm px-2 py-1 rounded-md font-medium">
            v{version}
          </span>
        </div>

        {/* Hover Overlay */}
        <div className={`absolute inset-0 bg-black/60 flex items-center justify-center transition-opacity duration-300 z-20 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}>
          <button
            onClick={handleViewDetails}
            className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 backdrop-blur-sm"
          >
            {t('plugins.view_details')}
          </button>
        </div>
      </div>
      
      {/* Plugin Info */}
      <div className="flex flex-1 flex-col p-4">
        {/* Name and Category */}
        <div className="mb-2">
          <h4 className="font-bold text-white text-lg mb-1 line-clamp-1">{name}</h4>
          <p className="text-purple-300 text-sm font-medium">
            {compatibility.join(' • ') || 'VST Plugin'}
          </p>
        </div>
        
        {/* Description */}
        <p className="text-gray-300 text-sm mb-3 line-clamp-2">{description}</p>
        
        {/* Features Preview */}
        {displayFeatures.length > 0 && (
          <div className="mb-3">
            {displayFeatures.map((feature, index) => (
              <p key={index} className="text-xs text-gray-400 line-clamp-1">
                • {feature}
              </p>
            ))}
            {features.length > 2 && (
              <p className="text-xs text-gray-500">
                +{features.length - 2} {t('plugins.more_features')}
              </p>
            )}
          </div>
        )}
        
        {/* Tags */}
        {displayTags.length > 0 && (
          <div className="mb-3 flex h-7 gap-1 overflow-hidden">
            {displayTags.map((tag, index) => (
              <span 
                key={index}
                className="min-w-0 shrink truncate border border-gray-600 text-xs text-gray-400 px-2 py-1 rounded-md hover:border-purple-500/50 hover:text-purple-300 transition-colors duration-200"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
        
        {/* Price and Buy Button. The caption sits under the amount so a longer
            translation or a wider price keeps the button on one line. */}
        <div className="mt-auto flex items-center justify-between gap-3">
          <div className="flex h-11 min-w-0 flex-col justify-center">
            {price === 0 ? (
              <span className="w-fit bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-md text-sm font-bold">
                FREE
              </span>
            ) : (
              <>
                <span className="truncate text-2xl font-bold leading-none tabular-nums text-white">
                  ${price.toFixed(2)}
                </span>
                <span className="mt-1 truncate text-xs leading-4 text-gray-400" title={t('plugins.one_time')}>
                  {t('plugins.one_time')}
                </span>
              </>
            )}
          </div>
          
          <button
            type="button"
            onClick={handleBuyNow}
            disabled={owned}
            className={`shrink-0 whitespace-nowrap transition-all duration-300 px-4 py-2 rounded-md text-sm font-semibold flex items-center space-x-2 ${
              owned
                ? "cursor-not-allowed bg-gray-700 text-gray-400"
                : isHovered
                ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg"
                : "bg-gray-700 hover:bg-gray-600 text-gray-300"
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.5 6M7 13l-1.5 6m0 0h9" />
            </svg>
            <span>{owned ? t('plugins.owned') : price === 0 ? t('plugins.free_download') : t('plugins.buy_now')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default PluginCardC; 